import { Injectable } from '@angular/core';
import {
    Firestore,
    collection,
    doc,
    docData,
    addDoc,
    updateDoc,
    query,
    where,
    getDocs,
    serverTimestamp,
} from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import { ISessionData } from '@soposopo/sopo-models';
import { isElectron } from 'src/app/shared/utils/platform.utils';
import AppDetailsElectron from 'src/app/plugins/app-details.plugin';

const SOPO_SESSION_ID_KEY = 'sopo_session_id';
const SOPO_DEVICE_ID_KEY = 'sopo_device_id';

@Injectable({ providedIn: 'root' })
export class SessionService {
    private isLoggingOut = false;

    constructor(private firestore: Firestore) {}

    getDeviceId(): string {
        let deviceId = localStorage.getItem(SOPO_DEVICE_ID_KEY);
        if (!deviceId) {
            deviceId = crypto.randomUUID();
            localStorage.setItem(SOPO_DEVICE_ID_KEY, deviceId);
        }
        return deviceId;
    }

    getPlatform(): string {
        if (isElectron()) return 'electron';
        return 'web';
    }

    async getDeviceInfo(): Promise<{ manufacturer: string; deviceVersion: string; appVersionNumber: string }> {
        if (isElectron()) {
            try {
                const appInfo = await AppDetailsElectron.getAppInfo();
                const platformNames: Record<string, string> = { win32: 'Windows', darwin: 'macOS', linux: 'Linux' };
                return {
                    manufacturer: appInfo.osPlatform
                        ? (platformNames[appInfo.osPlatform] || appInfo.osPlatform)
                        : 'Electron',
                    deviceVersion: appInfo.osVersion || '',
                    appVersionNumber: appInfo.version || '',
                };
            } catch (error) {
                console.error('Failed to get Electron app info:', error);
            }
            return { manufacturer: 'Electron', deviceVersion: '', appVersionNumber: '' };
        }

        const ua = navigator.userAgent;
        let browser = 'Unknown';
        let deviceVersion = '';

        if (ua.includes('Chrome') && !ua.includes('Edg')) {
            browser = 'Chrome';
            const match = ua.match(/Chrome\/([\d.]+)/);
            deviceVersion = match ? match[1] : '';
        } else if (ua.includes('Firefox')) {
            browser = 'Firefox';
            const match = ua.match(/Firefox\/([\d.]+)/);
            deviceVersion = match ? match[1] : '';
        } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
            browser = 'Safari';
            const match = ua.match(/Version\/([\d.]+)/);
            deviceVersion = match ? match[1] : '';
        } else if (ua.includes('Edg')) {
            browser = 'Edge';
            const match = ua.match(/Edg\/([\d.]+)/);
            deviceVersion = match ? match[1] : '';
        }

        const os = this.getOsFromUserAgent(ua);
        const manufacturer = os ? `${browser} - ${os}` : browser;

        return { manufacturer, deviceVersion, appVersionNumber: '' };
    }

    getStoredSessionId(): string | null {
        try {
            return localStorage.getItem(SOPO_SESSION_ID_KEY);
        } catch {
            return null;
        }
    }

    /**
     * Resume an existing active session or create a new one.
     */
    async resumeOrCreateSession(userId: string, deviceInfo: {
        fcmToken: string;
        appVersionNumber: string;
        manufacturer: string;
        deviceVersion: string;
    }): Promise<string> {
        const storedSessionId = this.getStoredSessionId();

        if (storedSessionId) {
            try {
                const sessionRef = doc(this.firestore, `users/${userId}/sessions/${storedSessionId}`);
                const sessionDoc = await getDocs(
                    query(
                        collection(this.firestore, `users/${userId}/sessions`),
                        where('__name__', '==', storedSessionId),
                        where('sessionEnd', '==', null)
                    )
                );
                if (!sessionDoc.empty) {
                    // Session still active - reuse
                    await updateDoc(sessionRef, { fcmToken: deviceInfo.fcmToken });
                    return storedSessionId;
                }
            } catch (error) {
                console.error('Error checking existing session:', error);
            }
        }

        return this.createSession(userId, deviceInfo);
    }

    async createSession(userId: string, deviceInfo: {
        fcmToken: string;
        appVersionNumber: string;
        manufacturer: string;
        deviceVersion: string;
    }): Promise<string> {
        const deviceId = this.getDeviceId();
        const platform = this.getPlatform();

        // End old sessions for same device
        await this.endOldSessionsForDevice(userId, deviceId);

        const sessionsRef = collection(this.firestore, `users/${userId}/sessions`);
        const sessionDoc = await addDoc(sessionsRef, {
            sessionStart: serverTimestamp(),
            sessionEnd: null,
            endReason: null,
            deviceId,
            platform,
            fcmToken: deviceInfo.fcmToken,
            appVersionNumber: deviceInfo.appVersionNumber,
            manufacturer: deviceInfo.manufacturer,
            deviceVersion: deviceInfo.deviceVersion,
        });

        const sessionId = sessionDoc.id;

        try {
            localStorage.setItem(SOPO_SESSION_ID_KEY, sessionId);
        } catch (error) {
            console.error('Failed to store session ID:', error);
        }

        return sessionId;
    }

    async endSession(userId: string, sessionId: string): Promise<void> {
        this.isLoggingOut = true;
        try {
            const sessionRef = doc(this.firestore, `users/${userId}/sessions/${sessionId}`);
            await updateDoc(sessionRef, {
                sessionEnd: serverTimestamp(),
                endReason: 'logout',
            });
        } catch (error) {
            console.error('Failed to end session:', error);
        }
        this.clearStoredSessionId();
    }

    async updateSessionField(userId: string, sessionId: string, fields: Partial<ISessionData>): Promise<void> {
        try {
            const sessionRef = doc(this.firestore, `users/${userId}/sessions/${sessionId}`);
            await updateDoc(sessionRef, fields);
        } catch (error) {
            console.error('Failed to update session field:', error);
        }
    }

    watchSession(userId: string, sessionId: string): Observable<ISessionData> {
        const sessionRef = doc(this.firestore, `users/${userId}/sessions/${sessionId}`);
        return docData(sessionRef) as Observable<ISessionData>;
    }

    getIsLoggingOut(): boolean {
        return this.isLoggingOut;
    }

    resetIsLoggingOut(): void {
        this.isLoggingOut = false;
    }

    clearStoredSessionId(): void {
        try {
            localStorage.removeItem(SOPO_SESSION_ID_KEY);
        } catch (error) {
            console.error('Failed to clear session ID:', error);
        }
    }

    private getOsFromUserAgent(ua: string): string {
        if (ua.includes('Windows')) return 'Windows';
        if (ua.includes('Mac OS X')) return 'macOS';
        if (ua.includes('Android')) return 'Android';
        if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
        if (ua.includes('Linux')) return 'Linux';
        return '';
    }

    private async endOldSessionsForDevice(userId: string, deviceId: string): Promise<void> {
        try {
            const sessionsRef = collection(this.firestore, `users/${userId}/sessions`);
            const oldSessions = await getDocs(
                query(sessionsRef, where('deviceId', '==', deviceId), where('sessionEnd', '==', null))
            );

            const updatePromises = oldSessions.docs.map(sessionDoc =>
                updateDoc(sessionDoc.ref, {
                    sessionEnd: serverTimestamp(),
                    endReason: 'new_session_same_device',
                })
            );
            await Promise.allSettled(updatePromises);
        } catch (error) {
            console.error('Failed to end old sessions:', error);
        }
    }
}
